import React, { useEffect, useMemo, useState } from "react";
import { Bluetooth, RotateCw } from "lucide-react";
import { API_BASE_CLIENT } from "../config/vars";
import { fetchClientSensorProvisioning } from "../services/sensors";
import CompactSelect from "./CompactSelect";
import EditFloatingActions from "./EditFloatingActions";
import { pushAndroidBackHandler } from "../utils/androidBack";
import {
    SensorBleProvisioner,
    buildBleDeviceName,
    buildIotUrl,
    isBluetoothSupported,
} from "../utils/sensorBleProvisioning";
import { thresholdsFromSensorConfig, deviceSettingsFromSensorConfig } from "../utils/sensorThresholds";

const NOTIFICATION_MODES = {
    app: "app",
    telegram: "telegram",
    viber: "viber",
    all: "all",
};

const SensorBleSetupModal = ({ sensor, onClose, onComplete }) => {
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [logs, setLogs] = useState([]);
    const [statusText, setStatusText] = useState("");
    const [provisioning, setProvisioning] = useState(null);
    const [networks, setNetworks] = useState([]);
    const [wifiSsid, setWifiSsid] = useState("");
    const [wifiPassword, setWifiPassword] = useState("");
    const [wifiSsidAlt, setWifiSsidAlt] = useState("");
    const [wifiPasswordAlt, setWifiPasswordAlt] = useState("");
    const [notificationMode, setNotificationMode] = useState(NOTIFICATION_MODES.app);
    const [telegramBotToken, setTelegramBotToken] = useState("");
    const [telegramChatId, setTelegramChatId] = useState("");
    const [viberAuthToken, setViberAuthToken] = useState("");
    const [viberReceiverId, setViberReceiverId] = useState("");
    const [provisioner, setProvisioner] = useState(null);

    const bleSupported = useMemo(() => isBluetoothSupported(), []);
    const expectedBleName = useMemo(() => (
        provisioning?.ble_device_name
        || buildBleDeviceName(provisioning?.ble_name_prefix || "GasBox", provisioning?.device_uid || sensor?.device_uid)
    ), [provisioning, sensor?.device_uid]);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            if (!sensor?.id) return;
            try {
                setLoading(true);
                setMessage("");
                const data = await fetchClientSensorProvisioning(sensor.id);
                if (!cancelled) {
                    setProvisioning(data);
                }
            } catch (error) {
                if (!cancelled) {
                    setMessage(`❌ ${error.message}`);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [sensor?.id]);

    useEffect(() => () => {
        if (provisioner) {
            provisioner.disconnect().catch(() => {});
        }
    }, [provisioner]);

    const appendLog = (line) => {
        setLogs((prev) => [...prev.slice(-20), line]);
    };

    const handleConnect = async () => {
        try {
            setLoading(true);
            setMessage("");
            const nextProvisioner = new SensorBleProvisioner({
                onLog: appendLog,
                onStatus: setStatusText,
            });
            await nextProvisioner.connect({
                namePrefix: provisioning?.ble_name_prefix || "GasBox",
                deviceName: expectedBleName,
                deviceUid: provisioning?.device_uid || sensor?.device_uid,
            });
            setProvisioner(nextProvisioner);

            let status = null;
            try {
                status = await nextProvisioner.getStatus();
            } catch (statusError) {
                appendLog(`get_status: ${statusError.message}`);
            }

            const expectedUid = String(provisioning?.device_uid || sensor?.device_uid || "").trim();
            const actualUid = String(status?.device_id || "").trim();
            if (expectedUid && actualUid && expectedUid.toLowerCase() !== actualUid.toLowerCase()) {
                await nextProvisioner.disconnect();
                setProvisioner(null);
                throw new Error(
                    `Підключено до ${actualUid}, а в системі зареєстровано ${expectedUid}. Перевірте DEVICE_HOSTNAME на ESP32 або ім'я в nRF Connect.`
                );
            }
            if (status?.wifi_ssid) {
                setWifiSsid(status.wifi_ssid);
            }
            if (status?.wifi_ssid_alt) {
                setWifiSsidAlt(status.wifi_ssid_alt);
            }
            setStep(2);
            setMessage(
                status
                    ? "✅ Сенсор підключено через Bluetooth. Вкажіть Wi-Fi і сповіщення."
                    : "✅ Bluetooth підключено. Вкажіть Wi-Fi і сповіщення (статус сенсора не прочитано)."
            );
        } catch (error) {
            setMessage(`❌ ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleScanWifi = async () => {
        if (!provisioner) return;
        try {
            setLoading(true);
            setMessage("");
            const response = await provisioner.scanWifi();
            setNetworks(Array.isArray(response?.networks) ? response.networks : []);
            if (!response?.networks?.length) {
                setMessage("Поруч не знайдено Wi-Fi мереж");
            }
        } catch (error) {
            setMessage(`❌ ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleApply = async () => {
        if (!provisioner || !provisioning) return;
        if (!String(wifiSsid || "").trim() || !String(wifiPassword || "").trim()) {
            setMessage("❌ Вкажіть Wi-Fi мережу та пароль");
            return;
        }
        if (notificationMode === NOTIFICATION_MODES.telegram || notificationMode === NOTIFICATION_MODES.all) {
            if (!telegramBotToken.trim() || !telegramChatId.trim()) {
                setMessage("❌ Для Telegram потрібні bot token і chat id");
                return;
            }
        }
        if (notificationMode === NOTIFICATION_MODES.viber || notificationMode === NOTIFICATION_MODES.all) {
            if (!viberAuthToken.trim() || !viberReceiverId.trim()) {
                setMessage("❌ Для Viber потрібні auth token і receiver id");
                return;
            }
        }

        const eventUrl = buildIotUrl(API_BASE_CLIENT, provisioning.iot_event_path);
        const deviceId = String(provisioning.device_uid || sensor.device_uid || "").trim().toLowerCase();

        try {
            setLoading(true);
            setMessage("");
            setStep(3);

            await provisioner.setWifi({
                ssid: wifiSsid.trim(),
                password: wifiPassword,
            });

            if (String(wifiSsidAlt || "").trim()) {
                if (!String(wifiPasswordAlt || "").trim()) {
                    throw new Error("Для альтернативної Wi-Fi мережі вкажіть пароль");
                }
                await provisioner.setWifiAlt({
                    ssid: wifiSsidAlt.trim(),
                    password: wifiPasswordAlt,
                });
            } else {
                await provisioner.setWifiAlt({ clear: true });
            }

            await provisioner.setBackend({
                enabled: true,
                alertUrl: eventUrl,
                heartbeatUrl: eventUrl,
                bearerToken: provisioning.auth_token,
                deviceId,
                networkId: provisioning.network_id,
                locationId: provisioning.location_id,
            });

            const telegramEnabled = notificationMode === NOTIFICATION_MODES.telegram || notificationMode === NOTIFICATION_MODES.all;
            const viberEnabled = notificationMode === NOTIFICATION_MODES.viber || notificationMode === NOTIFICATION_MODES.all;

            await provisioner.setTelegram({
                enabled: telegramEnabled,
                botToken: telegramEnabled ? telegramBotToken.trim() : "",
                chatId: telegramEnabled ? telegramChatId.trim() : "",
            });

            await provisioner.setViber({
                enabled: viberEnabled,
                authToken: viberEnabled ? viberAuthToken.trim() : "",
                receiverId: viberEnabled ? viberReceiverId.trim() : "",
            });

            await provisioner.setThresholds({
                ...thresholdsFromSensorConfig(sensor?.config),
                ...deviceSettingsFromSensorConfig(sensor?.config, sensor),
            });

            const commitResult = await provisioner.commit();
            await provisioner.disconnect();
            setProvisioner(null);

            if (commitResult?.wifi_pending) {
                setMessage("✅ Конфіг збережено. Wi-Fi підключиться після закриття Bluetooth.");
            } else if (commitResult?.wifi_connected === false) {
                setMessage("⚠️ Конфігурацію збережено, але сенсор не підключився до Wi-Fi. Перевірте пароль.");
            } else {
                setMessage("✅ Сенсор налаштовано. Він підключиться до мережі та сервера.");
            }

            if (typeof onComplete === "function") {
                await onComplete({
                    notificationMode,
                    wifiSsid: wifiSsid.trim(),
                    wifiSsidAlt: String(wifiSsidAlt || "").trim(),
                });
            }
        } catch (error) {
            setMessage(`❌ ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = async () => {
        if (provisioner) {
            await provisioner.disconnect().catch(() => {});
        }
        onClose();
    };

    useEffect(() => pushAndroidBackHandler(() => {
        if (provisioner) {
            provisioner.disconnect().catch(() => {});
        }
        onClose();
        return true;
    }), [provisioner, onClose]);

    return (
        <div className="modal-backdrop" onClick={handleClose}>
            <div className="modal-content client-sensors-modal client-sensors-ble-modal" onClick={(e) => e.stopPropagation()}>
                <div className="client-sensors-ble-header">
                    <h3><Bluetooth size={18} /> Налаштування сенсора через Bluetooth</h3>
                </div>

                {!bleSupported ? (
                    <div className="client-sensors-note-box">
                        Bluetooth недоступний. Відкрийте Android-додаток клієнта.
                    </div>
                ) : null}

                <div className="client-sensors-ble-steps">
                    <span className={step >= 1 ? "is-active" : ""}>1. Підключення</span>
                    <span className={step >= 2 ? "is-active" : ""}>2. Wi-Fi і сповіщення</span>
                    <span className={step >= 3 ? "is-active" : ""}>3. Збереження</span>
                </div>

                <div className="client-sensors-ble-meta">
                    <div><strong>Сенсор:</strong> {sensor?.display_name || sensor?.device_uid}</div>
                    <div><strong>Серійний номер:</strong> {sensor?.device_uid}</div>
                    <div><strong>Bluetooth ім&apos;я:</strong> {expectedBleName}</div>
                    {statusText ? <div><strong>Статус:</strong> {statusText}</div> : null}
                </div>

                {step === 1 ? (
                    <div className="client-sensors-form-grid client-sensors-ble-step">
                        <button className="add-btn" onClick={handleConnect} disabled={loading || !bleSupported || !provisioning}>
                            <Bluetooth size={16} /> Підключитися до сенсора
                        </button>
                    </div>
                ) : null}

                {step >= 2 ? (
                    <div className="client-sensors-form-grid client-sensors-ble-step">
                        <div className="client-sensors-ble-toolbar">
                            <button className="add-btn refresh" type="button" onClick={handleScanWifi} disabled={loading || !provisioner}>
                                <RotateCw size={16} /> Сканувати Wi-Fi
                            </button>
                        </div>
                        {networks.length ? (
                            <CompactSelect
                                className="client-sensors-form-span-2"
                                ariaLabel="Wi-Fi мережа"
                                value={wifiSsid}
                                placeholder="Оберіть мережу"
                                options={[
                                    { value: "", label: "Оберіть мережу" },
                                    ...networks.map((item) => ({
                                        value: item.ssid,
                                        label: `${item.ssid} (${item.rssi} dBm)${item.secure ? " • захищена" : ""}`,
                                    })),
                                ]}
                                onChange={(next) => setWifiSsid(next)}
                            />
                        ) : null}
                        <input
                            className="client-sensors-form-span-2"
                            type="text"
                            placeholder="Назва Wi-Fi (SSID)"
                            value={wifiSsid}
                            onChange={(e) => setWifiSsid(e.target.value)}
                        />
                        <input
                            className="client-sensors-form-span-2"
                            type="password"
                            placeholder="Пароль Wi-Fi"
                            value={wifiPassword}
                            onChange={(e) => setWifiPassword(e.target.value)}
                        />

                        {networks.length ? (
                            <CompactSelect
                                className="client-sensors-form-span-2"
                                ariaLabel="Альтернативна Wi-Fi мережа"
                                value={wifiSsidAlt}
                                placeholder="Альтернативна мережа (опційно)"
                                options={[
                                    { value: "", label: "Без альтернативної мережі" },
                                    ...networks.map((item) => ({
                                        value: item.ssid,
                                        label: `${item.ssid} (${item.rssi} dBm)${item.secure ? " • захищена" : ""}`,
                                    })),
                                ]}
                                onChange={(next) => setWifiSsidAlt(next)}
                            />
                        ) : null}
                        <input
                            className="client-sensors-form-span-2"
                            type="text"
                            placeholder="Альтернативний SSID (опційно)"
                            value={wifiSsidAlt}
                            onChange={(e) => setWifiSsidAlt(e.target.value)}
                        />
                        <input
                            className="client-sensors-form-span-2"
                            type="password"
                            placeholder="Пароль альтернативної Wi-Fi"
                            value={wifiPasswordAlt}
                            onChange={(e) => setWifiPasswordAlt(e.target.value)}
                            disabled={!String(wifiSsidAlt || "").trim()}
                        />

                        <label className="client-sensors-form-span-2 client-sensors-ble-label">
                            Сповіщення
                            <CompactSelect
                                ariaLabel="Куди надсилати сповіщення"
                                value={notificationMode}
                                options={[
                                    { value: NOTIFICATION_MODES.app, label: "Лише у програму (push)" },
                                    { value: NOTIFICATION_MODES.telegram, label: "Telegram" },
                                    { value: NOTIFICATION_MODES.viber, label: "Viber" },
                                    { value: NOTIFICATION_MODES.all, label: "Telegram + Viber + програма" },
                                ]}
                                onChange={(next) => setNotificationMode(next)}
                            />
                        </label>

                        {(notificationMode === NOTIFICATION_MODES.telegram || notificationMode === NOTIFICATION_MODES.all) ? (
                            <>
                                <input
                                    className="client-sensors-form-span-2"
                                    type="text"
                                    placeholder="Telegram bot token"
                                    value={telegramBotToken}
                                    onChange={(e) => setTelegramBotToken(e.target.value)}
                                />
                                <input
                                    className="client-sensors-form-span-2"
                                    type="text"
                                    placeholder="Telegram chat id"
                                    value={telegramChatId}
                                    onChange={(e) => setTelegramChatId(e.target.value)}
                                />
                            </>
                        ) : null}

                        {(notificationMode === NOTIFICATION_MODES.viber || notificationMode === NOTIFICATION_MODES.all) ? (
                            <>
                                <input
                                    className="client-sensors-form-span-2"
                                    type="text"
                                    placeholder="Viber auth token"
                                    value={viberAuthToken}
                                    onChange={(e) => setViberAuthToken(e.target.value)}
                                />
                                <input
                                    className="client-sensors-form-span-2"
                                    type="text"
                                    placeholder="Viber receiver id"
                                    value={viberReceiverId}
                                    onChange={(e) => setViberReceiverId(e.target.value)}
                                />
                            </>
                        ) : null}

                    </div>
                ) : null}

                {message ? <div className="message-banner">{message}</div> : null}

                {logs.length ? (
                    <div className="client-sensors-ble-log">
                        {logs.map((line, index) => (
                            <div key={`${index}-${line}`}>{line}</div>
                        ))}
                    </div>
                ) : null}

                <EditFloatingActions
                    onSave={step >= 2 && provisioner ? handleApply : null}
                    onCancel={handleClose}
                    saveDisabled={loading}
                    cancelDisabled={false}
                    saveLabel="Зберегти на сенсорі"
                    cancelLabel="Закрити"
                />
            </div>
        </div>
    );
};

export default SensorBleSetupModal;
